import { useState, useCallback, useEffect } from 'react';
import { Project, ProjectCreateInput } from '../types/project';
import { ProjectApiClient } from '../utils/api-client';

export const useProjects = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const apiClient = new ProjectApiClient();

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.fetchProjects();
      if (response.error) {
        throw new Error(response.error);
      }
      setProjects(response.data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : '案件の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  const createProject = useCallback(async (projectData: ProjectCreateInput) => {
    try {
      const response = await apiClient.createProject(projectData);
      if (response.error) {
        throw new Error(response.error);
      }
      await fetchProjects();
      return response.data;
    } catch (err) {
      throw new Error(err instanceof Error ? err.message : '案件の作成に失敗しました');
    }
  }, [fetchProjects]);

  const updateProject = useCallback(async (id: string, projectData: Partial<ProjectCreateInput>) => {
    try {
      const response = await apiClient.updateProject(id, projectData);
      if (response.error) {
        throw new Error(response.error);
      }
      await fetchProjects();
      return response.data;
    } catch (err) {
      throw new Error(err instanceof Error ? err.message : '案件の更新に失敗しました');
    }
  }, [fetchProjects]);

  const deleteProject = useCallback(async (id: string) => {
    try {
      const response = await apiClient.deleteProject(id);
      if (response.error) {
        throw new Error(response.error);
      }
      await fetchProjects();
    } catch (err) {
      throw new Error(err instanceof Error ? err.message : '案件の削除に失敗しました');
    }
  }, [fetchProjects]);

  useEffect(() => {
    fetchProjects();
    const interval = setInterval(fetchProjects, 60000); // 1分ごとに更新
    return () => clearInterval(interval);
  }, [fetchProjects]);

  return {
    projects,
    loading,
    error,
    createProject,
    updateProject,
    deleteProject,
    refetch: fetchProjects,
  };
};